import { FieldConfig } from '@components/application/DynamicForm/types';

export type ChannelFormData = {
  [key: string]: any;
};

/**
 * Gets the label for a connector in the specified language.
 * Falls back to the first available language if the requested language is not available.
 * Returns the connector id if no label is available.
 * 
 * @param localizable - The object with id and label properties
 * @param language - Optional language code (e.g., 'en', 'es'). If not provided, uses 'en' as default
 * @returns The label string for the requested language, or fallback
 */
export const getLocalizableLabel = (
  localizable?: { id?: string; label?: {[key: string]: string | undefined }},
  language?: string
): string => {
  if ( !localizable ) {
    return '';
  }

  const label = localizable.label;
  
  // If label is not an object (for backward compatibility or undefined), return id or empty
  if ( !label || typeof label !== 'object' || Array.isArray( label )) {
    return localizable.id || '';
  }

  // If language is specified and exists, return it
  if ( language && label[language]) {
    return label[language];
  }

  // Fallback to first available language key
  const availableLanguages = Object.keys( label );
  if ( availableLanguages.length > 0 ) {
    return label?.[availableLanguages[0]] || '';
  }

  // Final fallback to connector id
  return localizable.id || '';
};

const generateEmptyData = ( fieldConfig: FieldConfig[]): ChannelFormData => {
  const emptyData: ChannelFormData = {};
  
  fieldConfig.forEach( field => {
    switch ( field.type ) {
      case 'string':
        emptyData[field.name] = '';
        break;
      case 'number':
      case 'numberUnit':
        emptyData[field.name] = 0;
        break;
      case 'boolean':
        emptyData[field.name] = true;
        break;
      case 'select':
        // Select the first option if available
        emptyData[field.name] = field.options?.[0]?.value || '';
        break;
      case 'multiSelect':
      case 'multiNumberUnit':
      case 'object':
      case 'multiObject':
        emptyData[field.name] = undefined;
        break;
      default:
        emptyData[field.name] = '';
        break;
    }
  });
  
  return emptyData;
};

export type ChannelFormType = 'publication' | 'defaultValues';

export const generateChannelFormStructure = async ( connectorId: string, language: string = 'es', backend, formType: ChannelFormType = 'publication' ): Promise<{
  emptyData: ChannelFormData;
  fieldConfig: FieldConfig[]
}> => {
  try {
    const fetcher = formType === 'publication'
      ? backend.get.get_channels_publication_form_type
      : backend.get.get_channels_publication_default_values_type;
    const fieldConfig = await fetcher({ connectorId, language });
    const filteredFieldConfig = fieldConfig.filter( field => !field.isReadOnly );
    const emptyData = generateEmptyData( fieldConfig );

    return {
      emptyData,
      fieldConfig: filteredFieldConfig
    };
  } catch ( error ) {
    console.error( 'Error fetching channel form structure:', error );
    return {
      emptyData: {},
      fieldConfig: []
    };
  }
};